import { traceIa } from '../iaTrace';
import { CgDocType } from '../ocr/ocrResponseTypes';
import { TaxDataSnapshot, UploadedDocResult } from '../store/useFormCGIAStore';

/**
 * Moteur de cross-validation pur (front-only, testable sans dépendance).
 *
 * Exécute un ensemble de règles métier sur les résultats OCR des documents
 * uploadés. Retourne un rapport structuré `CrossCheckReport`.
 *
 * Les règles vivent dans `crossCheckRules.ts` — séparer la définition des règles
 * et l'exécution permet d'ajouter facilement des règles v2 sans toucher au moteur.
 */

export type Severity = 'error' | 'warning' | 'info';

export interface CrossCheckFinding {
  ruleId: string;
  severity: Severity;
  message: string;
  /** Clés de champs à surligner dans le ReviewFormStep. */
  fields?: string[];
  /**
   * Documents uploadés concernés par cette vérification. Utilisé par le
   * CrossCheckBanner pour proposer à l'agence un bouton "Modifier ce
   * document" quand la correction passe par un re-upload plutôt que par une
   * édition du formulaire (ex: SIRET mandat non extrait, CT trop ancien,
   * assurance qui est un devis, etc.).
   */
  documentTypes?: CgDocType[];
}

export interface CrossCheckReport {
  hasBlockingErrors: boolean;
  errors: CrossCheckFinding[];
  warnings: CrossCheckFinding[];
  infos: CrossCheckFinding[];
}

/**
 * Une règle métier. Le `check` prend le contexte des docs uploadés et retourne
 * soit `null` (règle OK) soit un finding à afficher à l'utilisateur.
 */
export interface CrossCheckRule {
  id: string;
  description: string;
  severity: Severity;
  check: (ctx: CrossCheckContext) => CrossCheckFinding | null;
}

export interface CrossCheckContext {
  docs: Partial<Record<string, UploadedDocResult>>;
  now: Date;
  /**
   * Saisie utilisateur de l'étape 1 (PlateAndDepartmentScreen).
   * Permet de cross-checker la plaque/département saisis par l'agence
   * avec les valeurs OCR extraites des pièces — essentiel car si l'agence
   * a tapé une plaque qui ne correspond PAS aux pièces déposées, ANTS
   * rejettera systématiquement le dossier.
   */
  userInput?: {
    regPlate?: string;
    department?: string;
  };
  /**
   * Données MiSIV retournées par /api/misiv/ecg à l'étape 1. Source de
   * vérité externe pour le VIN, la marque et la date de mise en circulation.
   * Permet de cross-checker les valeurs OCR contre la base SIV officielle.
   */
  taxData?: TaxDataSnapshot | null;
  /**
   * Co-titulaire VENDEUR (C.4.1 de la carte grise) déclaré MANUELLEMENT par
   * l'agent (case dédiée des écrans upload / review) quand l'OCR de la carte
   * grise ne l'a pas lu. Force la règle `signature_cession_cotitulaire` à
   * réclamer la vérification de sa signature côté vendeur sur la cession.
   */
  cotitulaireVendeurManuel?: boolean;
  /**
   * Nombre de fichiers déposés pour la pièce d'identité du co-titulaire
   * ACQUÉREUR (zone d'upload dédiée du bloc co-titulaire).
   *
   * ⚠️ Ces fichiers sont uploadés SANS OCR (`SimpleUploadZone`) : ils ne
   * produisent aucune extraction, n'apparaissent donc pas dans `ctx.docs` et
   * aucune règle ne peut inspecter leur contenu. Le seul signal exploitable est
   * leur NOMBRE — d'où ce champ, qui permet au moins de réclamer les deux faces.
   *
   * Retour François 2026-07-20 (EQ-598-TE) : « Pièce d'identité du cotitulaire
   * verso manquant et non remonté ».
   */
  cotitulaireAcheteurFilesCount?: number;
}

/**
 * Exécute une liste de règles sur un contexte et agrège les résultats.
 */
export const runCrossChecks = (
  rules: CrossCheckRule[],
  context: CrossCheckContext,
  options: { trace?: boolean } = {},
): CrossCheckReport => {
  const errors: CrossCheckFinding[] = [];
  const warnings: CrossCheckFinding[] = [];
  const infos: CrossCheckFinding[] = [];
  const { trace } = options;

  if (trace) {
    const docsList = Object.keys(context.docs);
    traceIa(
      'CROSS-CHECKS',
      `Exécution de ${rules.length} règles de cohérence inter-pièces (outil: moteur de règles métier frontend pur, ` +
        `crossCheckRules.ts — AUCUN appel IA, comparaisons déterministes sur les valeurs déjà extraites). ` +
        `Pièces disponibles: ${docsList.length > 0 ? `[${docsList.join(', ')}]` : 'aucune'}. ` +
        `Plaque saisie étape 1: ${context.userInput?.regPlate ?? '—'}, département: ${context.userInput?.department ?? '—'}, ` +
        `données MiSIV (source de vérité SIV externe pour VIN/MEC): ${context.taxData ? 'présentes' : 'absentes'}.`,
    );
  }

  rules.forEach((rule) => {
    try {
      const finding = rule.check(context);
      if (trace) {
        if (!finding) {
          traceIa('CROSS-CHECKS', `Règle "${rule.id}" (${rule.description}) → OK, rien à signaler.`);
        } else {
          const docsPart =
            finding.documentTypes && finding.documentTypes.length > 0
              ? ` [pièces concernées: ${finding.documentTypes.join(', ')}]`
              : '';
          const fieldsPart =
            finding.fields && finding.fields.length > 0 ? ` [champs: ${finding.fields.join(', ')}]` : '';
          traceIa(
            'CROSS-CHECKS',
            `Règle "${rule.id}" (${rule.description}) → ${finding.severity.toUpperCase()}: ${finding.message}${docsPart}${fieldsPart}`,
          );
        }
      }
      if (!finding) return;
      if (finding.severity === 'error') errors.push(finding);
      else if (finding.severity === 'warning') warnings.push(finding);
      else infos.push(finding);
    } catch (err) {
      // JSON.stringify(Error) === '{}' → log message + stack explicites.
      const detail = err instanceof Error ? `${err.message}\n${err.stack ?? ''}` : JSON.stringify(err);
      console.error(`Cross-check rule ${rule.id} threw:`, detail);
      if (trace) {
        traceIa('CROSS-CHECKS', `Règle "${rule.id}" (${rule.description}) → EXCEPTION (règle ignorée): ${detail}`);
      }
      // Une règle qui explose ne doit pas bloquer les autres
    }
  });

  if (trace) {
    traceIa(
      'CROSS-CHECKS',
      `Bilan: ${errors.length} blocage(s), ${warnings.length} point(s) d'attention, ${infos.length} info(s). ` +
        `hasBlockingErrors=${errors.length > 0}.`,
    );
  }

  return {
    hasBlockingErrors: errors.length > 0,
    errors,
    warnings,
    infos,
  };
};

/**
 * Helpers utilitaires partagés par plusieurs règles.
 */

export const normalizeForCompare = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // retire les accents
    .replace(/\s+/g, ' ')
    .replace(/-/g, ' ');
};

export const getFieldValue = <T = unknown>(doc: UploadedDocResult | undefined, fieldKey: string): T | null => {
  // Robustesse : un doc hydraté depuis le backend (mode édition agence) peut
  // arriver avec ocrResponse=undefined ou fields=undefined si l'adapter est
  // incomplet. On retourne null silencieusement plutôt que de faire crasher
  // les 35 règles cross-check via un TypeError silencieusement catch.
  const field = doc?.ocrResponse?.fields?.[fieldKey];
  return (field?.value as T) ?? null;
};

/** Confiance OCR (0-1) d'un champ, ou null si doc/champ absent. */
export const getFieldConfidence = (doc: UploadedDocResult | undefined, fieldKey: string): number | null => {
  const field = doc?.ocrResponse?.fields?.[fieldKey];
  return typeof field?.confidence === 'number' ? field.confidence : null;
};

/**
 * Source OCR d'un champ : `extracted` (lu directement sur l'image),
 * `inferred` (déduit par le modèle à partir d'indices contextuels —
 * confiance souvent < 1) ou `not_found`. Utile pour rétrograder les règles
 * dures (« trop ancien », « manquant », etc.) quand l'OCR a juste deviné.
 */
export const getFieldSource = (
  doc: UploadedDocResult | undefined,
  fieldKey: string,
): 'extracted' | 'inferred' | 'not_found' | null => {
  const field = doc?.ocrResponse?.fields?.[fieldKey];
  return field?.source ?? null;
};

export const monthsBetween = (from: Date, to: Date): number => {
  const diffMs = to.getTime() - from.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return diffDays / 30.44; // moyenne astronomique
};

export const parseDateMaybe = (value: unknown): Date | null => {
  if (typeof value !== 'string') return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Paires de caractères fréquemment confondues à la lecture OCR (documents
 * manuscrits, scans dégradés). Clés stockées triées par code point
 * ("0O", "JL", …) pour une recherche symétrique.
 */
const OCR_CONFUSABLE_PAIRS = new Set([
  '0O', '0D', '0Q', 'DO', '1I', '1L', '1T', '1A', '14', '2Z', '4A', '5S', '6G', '6C', '7T', '8B',
  'IJ', 'IL', 'JL', 'UV', 'EF', 'CG', 'MN', 'KX',
  // "s" minuscule lu "e" sur les cessions remplies sans tirets en minuscules
  // (ds063ch lu de063ch) — patch DC-39460.
  'ES',
]);

// Caractères "fins" fréquemment AJOUTÉS ou OMIS par l'OCR (un trait parasite lu
// "1", un "I" avalé…). Sert à juger qu'une insertion/suppression d'un seul
// caractère relève du bruit de lecture plutôt que d'une vraie divergence.
const OCR_THIN_NOISE_CHARS = new Set(['1', 'I', 'L', 'T', 'J', '7']);

const areCharsConfusable = (a: string, b: string): boolean =>
  OCR_CONFUSABLE_PAIRS.has([a, b].sort().join(''));

/**
 * true si deux valeurs (déjà normalisées via normalizeForCompare) ne
 * diffèrent que d'UN caractère appartenant à une paire OCR-confusable
 * (ex: DS-513-PJ vs DS-513-PL). Signal fort d'une erreur de LECTURE sur
 * l'une des pièces plutôt que d'un vrai document divergent — les règles
 * downgradent alors la sévérité au lieu de bloquer.
 */
/**
 * Si deux valeurs (même longueur) ne diffèrent que d'UN caractère appartenant à
 * une paire OCR-confusable, retourne le détail de cette divergence ({from, to,
 * index}) ; sinon null. `from` = caractère de `a`, `to` = caractère de `b`.
 * Permet de nommer le caractère exact dans les messages (« B lu au lieu de 8 »).
 */
export const findSingleOcrConfusion = (
  a: string,
  b: string,
): { from: string; to: string; index: number } | null => {
  const ua = a.toUpperCase();
  const ub = b.toUpperCase();
  if (ua.length !== ub.length) return null;
  const diffs = [...ua].reduce<Array<{ from: string; to: string; index: number }>>((acc, char, i) => {
    if (char !== ub[i]) acc.push({ from: char, to: ub[i], index: i });
    return acc;
  }, []);
  if (diffs.length === 1 && areCharsConfusable(diffs[0].from, diffs[0].to)) return diffs[0];
  return null;
};

export const isSingleOcrConfusion = (a: string, b: string): boolean => findSingleOcrConfusion(a, b) !== null;

/**
 * Opérations d'édition (Levenshtein + backtrace) transformant `a` en `b`.
 * Sert à diagnostiquer si deux VIN ne diffèrent que par du bruit OCR — y compris
 * les cas de DÉCALAGE (un caractère parasite inséré qui décale la lecture), que
 * `findSingleOcrConfusion` (substitution sur une position alignée) ne couvre pas.
 */
type VinEdit =
  | { kind: 'sub'; from: string; to: string }
  | { kind: 'ins'; char: string; bIndex: number }
  | { kind: 'del'; char: string; aIndex: number };

const vinEditOperations = (a: string, b: string): VinEdit[] => {
  const m = a.length;
  const n = b.length;
  const d: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = 0; i <= m; i++) d[i][0] = i;
  for (let j = 0; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
    }
  }
  const ops: VinEdit[] = [];
  let i = m;
  let j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1] && d[i][j] === d[i - 1][j - 1]) {
      i -= 1;
      j -= 1; // match
    } else if (i > 0 && j > 0 && d[i][j] === d[i - 1][j - 1] + 1) {
      ops.push({ kind: 'sub', from: a[i - 1], to: b[j - 1] });
      i -= 1;
      j -= 1;
    } else if (j > 0 && d[i][j] === d[i][j - 1] + 1) {
      ops.push({ kind: 'ins', char: b[j - 1], bIndex: j - 1 });
      j -= 1;
    } else {
      ops.push({ kind: 'del', char: a[i - 1], aIndex: i - 1 });
      i -= 1;
    }
  }
  return ops;
};

/**
 * true si deux VIN ne diffèrent que par du BRUIT OCR plausible (≤ 2 éditions) :
 *  - substitution sur une paire confusable (8/B, A/1, 0/O/D…),
 *  - lettre DOUBLÉE mal lue (ZZZ lu ZZ),
 *  - trait fin parasite inséré/omis (1, I, L…).
 * Couvre le cas de DÉCALAGE (cession « W1AUZZ… » vs CG « WAUZZZ… » — DC-39240),
 * sans suppression abusive : une vraie divergence (caractères non confusables,
 * ex. 8→9) reste détectée. La carte grise (imprimée, SIV) fait alors foi.
 */
export const isProbableVinOcrVariant = (a: string, b: string): boolean => {
  const ua = a.toUpperCase().trim();
  const ub = b.toUpperCase().trim();
  if (ua === ub) return false;
  if (Math.abs(ua.length - ub.length) > 1) return false;

  const ops = vinEditOperations(ua, ub);
  if (ops.length === 0 || ops.length > 2) return false;

  const isAdjacentDuplicate = (s: string, idx: number, char: string): boolean =>
    s[idx - 1] === char || s[idx + 1] === char;

  return ops.every((op) => {
    if (op.kind === 'sub') return areCharsConfusable(op.from, op.to);
    if (op.kind === 'ins') return OCR_THIN_NOISE_CHARS.has(op.char) || isAdjacentDuplicate(ub, op.bIndex, op.char);
    return OCR_THIN_NOISE_CHARS.has(op.char) || isAdjacentDuplicate(ua, op.aIndex, op.char);
  });
};

export const SKIPCAR_SIRET = '89918218200027';
// Le champ « N° SIRET » du mandat Cerfa 13757 attend en pratique un SIREN
// (9 chiffres) : c'est le SIREN qui identifie l'entité SKIPCAR, le NIC (5
// derniers chiffres) désignant un établissement et n'étant pas discriminant.
export const SKIPCAR_SIREN = SKIPCAR_SIRET.slice(0, 9); // 899182182
export const IMMAT_SIV_REGEX = /^[A-Z]{2}-[0-9]{3}-[A-Z]{2}$/;
export const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/;
export const SIREN_REGEX = /^[0-9]{9}$/;
